<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useBranding } from 'shared/composables/useBranding';
import NewWebhook from './NewWebHook.vue';
import EditWebhook from './EditWebHook.vue';
import WebhookRow from './WebhookRow.vue';

export default {
  components: {
    NewWebhook,
    EditWebhook,
    WebhookRow,
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return { replaceInstallationName };
  },
  data() {
    return {
      loading: {},
      showAddPopup: false,
      showEditPopup: false,
      showDeleteConfirmationPopup: false,
      selectedWebHook: {},
    };
  },
  computed: {
    ...mapGetters({
      records: 'webhooks/getWebhooks',
      uiFlags: 'webhooks/getUIFlags',
    }),
    integration() {
      return this.$store.getters['integrations/getIntegration']('webhook');
    },
  },
  mounted() {
    this.$store.dispatch('webhooks/get');
  },
  methods: {
    openAddPopup() {
      this.showAddPopup = true;
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },
    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selectedWebHook = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    openEditPopup(webhook) {
      this.showEditPopup = true;
      this.selectedWebHook = webhook;
    },
    hideEditPopup() {
      this.showEditPopup = false;
    },
    confirmDeletion() {
      this.loading[this.selectedWebHook.id] = true;
      this.closeDeletePopup();
      this.deleteWebhook(this.selectedWebHook.id);
    },
    async deleteWebhook(id) {
      try {
        await this.$store.dispatch('webhooks/delete', id);
        useAlert(
          this.$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.API.SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          this.$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.API.ERROR_MESSAGE')
        );
      }
    },
    goBack() {
      this.$router.push({ name: 'settings_applications' });
    },
  },
};
</script>

<template>
  <div class="webhooks-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="page-header flex items-center justify-between px-6 border-b border-slate-200 flex-shrink-0"
      style="height: 50px"
    >
      <div class="flex items-center gap-4">
        <button class="back-btn" @click="goBack">
          <span class="i-ph-arrow-left-bold size-4" />
          <span>{{ $t('INTEGRATION_SETTINGS.HEADER') }}</span>
        </button>
        <span class="text-slate-300">•</span>
        <h1 class="text-lg font-semibold text-slate-800">Webhooks</h1>
      </div>
      <button class="primary-action-btn" @click="openAddPopup">
        <span class="i-ph-plus-bold size-4" />
        {{ $t('INTEGRATION_SETTINGS.WEBHOOK.HEADER_BTN_TXT') }}
      </button>
    </header>

    <!-- Description -->
    <div class="px-6 py-3 border-b border-slate-200">
      <p class="text-sm text-n-slate-11">
        {{ replaceInstallationName(integration?.description || '') }}
      </p>
    </div>

    <!-- Stats -->
    <div class="px-6 py-3 border-b border-slate-200">
      <div class="text-sm text-n-slate-11">
        {{ records.length }} webhooks configured
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-6">
      <!-- Loading -->
      <div
        v-if="uiFlags.fetchingList"
        class="flex items-center justify-center py-20"
      >
        <div class="loading-spinner" />
      </div>

      <!-- Empty State -->
      <div v-else-if="!records.length" class="empty-state">
        <div class="empty-icon-wrap">
          <span class="i-ph-webhooks-logo-bold size-12" />
        </div>
        <h3 class="text-xl font-semibold text-slate-800 mb-2">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.LIST.404') }}
        </h3>
        <p class="text-n-slate-11 text-sm mb-6">
          Create your first webhook to receive real-time updates
        </p>
        <button class="primary-action-btn" @click="openAddPopup">
          <span class="i-ph-plus-bold size-4" />
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.HEADER_BTN_TXT') }}
        </button>
      </div>

      <!-- Webhooks List -->
      <div v-else class="space-y-3">
        <div
          v-for="webhook in records"
          :key="webhook.id"
          class="webhook-card group"
        >
          <div class="flex items-center gap-4 flex-1">
            <div class="webhook-icon">
              <span class="i-ph-webhooks-logo-bold size-5" />
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-sm font-semibold text-slate-800 truncate">
                {{ webhook.url }}
              </h3>
              <p class="text-xs text-n-slate-11">
                {{ webhook.subscriptions?.join(', ') || 'All events' }}
              </p>
            </div>
          </div>
          <div
            class="webhook-actions opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2"
          >
            <button
              class="action-btn"
              title="Edit"
              @click="openEditPopup(webhook)"
            >
              <span class="i-ph-pencil-simple-bold size-4" />
            </button>
            <button
              class="action-btn action-btn-danger"
              title="Delete"
              @click="openDeletePopup(webhook)"
            >
              <span class="i-ph-trash-bold size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <NewWebhook v-if="showAddPopup" :on-close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditWebhook
        v-if="showEditPopup"
        :id="selectedWebHook.id"
        :value="selectedWebHook"
        :on-close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.TITLE')"
      :message="
        $t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.MESSAGE', {
          webhookURL: selectedWebHook.url,
        })
      "
      :confirm-text="$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.YES')"
      :reject-text="$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.NO')"
    />
  </div>
</template>

<style scoped>
.webhooks-page {
  background-color: #ffffff !important;
}

.dark .webhooks-page {
  background-color: #ffffff !important;
}

.page-header {
  background-color: #ffffff !important;
  border-bottom-color: #e5e7eb !important;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.back-btn:hover {
  color: #111827;
  background: #f9fafb;
}

.primary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  background-color: #1f2937;
  color: #fff;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.primary-action-btn:hover {
  background-color: #374151;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #1f2937;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-icon-wrap {
  width: 100px;
  height: 100px;
  border-radius: 24px;
  background: linear-gradient(135deg, #f3f4f6 0%, #f9fafb 100%);
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  margin-bottom: 24px;
}

.webhook-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.webhook-card:hover {
  border-color: #d1d5db;
  background: #f9fafb;
}

.webhook-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f3f4f6 0%, #f9fafb 100%);
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4b5563;
  flex-shrink: 0;
}

.action-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  border: none;
  border-radius: 8px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
}

.action-btn:hover {
  background: #f3f4f6;
  color: #111827;
}

.action-btn-danger:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

/* Keep webhook route identical to other integration pages */
:deep(.modal-mask) {
  background: rgba(15, 23, 42, 0.45) !important;
  backdrop-filter: blur(4px);
}

:deep(.modal-mask .modal-container) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}

:deep(.modal-mask .modal-header),
:deep(.modal-mask .modal-content),
:deep(.modal-mask form),
:deep(.modal-mask .content) {
  background: #ffffff !important;
}

:deep(.modal-mask .woot-modal-header),
:deep(.modal-mask .woot-modal-header h3),
:deep(.modal-mask .woot-modal-header p),
:deep(.modal-mask .woot-modal-header .header-title),
:deep(.modal-mask .woot-modal-header .header-content) {
  background: #ffffff !important;
  color: #111827 !important;
}

:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.bg-n-background),
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2) {
  background: #ffffff !important;
}
:deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
:deep(label) {
  color: #111827 !important;
}
:deep(input:not(.reset-base)),
:deep(select:not(.reset-base)),
:deep(textarea:not(.reset-base)) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}
</style>
